import type { CompanyCategory, PipelineStage } from '@/modules/sales-research/types';

export type DraftStatus =
  | 'pending' | 'approved' | 'rejected' | 'scheduled' | 'sent' | 'failed' | 'skipped';

export interface PipelineRow {
  id: string;
  name: string;
  category: CompanyCategory;
  primary_city: string | null;
  status: PipelineStage;
  score: number | null;
  exists_in_zoho: boolean;
  nearest_outlet_id: string | null;
  nearest_distance_m: number | null;
  nearest_outlet_name: string | null;
  warm_orders: number | null;
  last_outbound_at: string | null;
  last_inbound_at: string | null;
  followups_sent: number;
  next_followup_at: string | null;
  is_stopped: boolean | null;
  last_sentiment: string | null;
  assigned_name: string | null;
  contact_count: number;
  pending_drafts: number;
  updated_at: string;
}

export interface OutreachDraft {
  id: string;
  company_id: string;
  contact_id: string | null;
  outlet_id: string | null;
  offer_id: string | null;
  channel: 'email' | 'whatsapp';
  sequence_step: number;
  status: DraftStatus;
  to_email: string | null;
  to_name: string | null;
  subject: string | null;
  body: string;
  original_subject: string | null;
  original_body: string | null;
  is_edited: boolean;
  rationale: string | null;
  personalization: Record<string, unknown> | null;
  model: string | null;
  generated_by: string;
  approver: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
  sent_at: string | null;
  error: string | null;
  created_at: string;
  company_name: string;
  company_category: CompanyCategory;
  company_city: string | null;
  company_score: number | null;
  contact_name: string | null;
  contact_title: string | null;
  contact_role: string | null;
  outlet_name: string | null;
  offer_name: string | null;
}

export interface Activity {
  id: number;
  company_id: string | null;
  contact_id: string | null;
  draft_id: string | null;
  actor: string;
  action: string;
  channel: 'email' | 'whatsapp' | null;
  direction: string | null;
  subject: string | null;
  body: string | null;
  meta: Record<string, unknown> | null;
  occurred_at: string;
}

export interface Offer {
  id: string;
  name: string;
  vertical: string;
  city: string | null;
  category: CompanyCategory | null;
  headline: string | null;
  body: string;
  price_note: string | null;
  cta: string | null;
  is_published: boolean;
  published_at: string | null;
  updated_at: string;
}

export interface Template {
  id: string;
  name: string;
  vertical: string;
  channel: 'email' | 'whatsapp';
  stage: string;
  subject: string | null;
  body: string;
  is_active: boolean;
  updated_at: string;
}

export interface AgentRun {
  id: string;
  kind: string;
  status: string;
  dry_run: boolean;
  companies_considered: number;
  drafts_created: number;
  model: string | null;
  cost_usd: number;
  started_at: string;
  finished_at: string | null;
  error: string | null;
}

export interface MailSender {
  address: string;
  label: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
}

/** Full stage list (used by the drawer's stage dropdown). */
export const STAGE_ORDER: PipelineStage[] = [
  'researched', 'contact_found', 'drafted', 'approved', 'sent',
  'replied', 'meeting', 'trial', 'won', 'lost',
];

/** Condensed board columns — a readable 6-column pipeline that fits the screen.
 *  Internal AI states (drafted/approved) fold into Contact found; meeting folds
 *  into Replied; lost/disqualified are off-board. */
export const BOARD_COLUMNS: PipelineStage[] = [
  'researched', 'contact_found', 'sent', 'replied', 'trial', 'won',
];

export function boardColumn(status: PipelineStage): PipelineStage | null {
  switch (status) {
    case 'researched': return 'researched';
    case 'contact_found':
    case 'drafted':
    case 'approved': return 'contact_found';
    case 'sent': return 'sent';
    case 'replied':
    case 'meeting': return 'replied';
    case 'trial': return 'trial';
    case 'won': return 'won';
    default: return null; // lost / disqualified — off the board
  }
}

export const STAGE_LABEL: Record<string, string> = {
  researched: 'Researched',
  contact_found: 'Contact found',
  drafted: 'Drafted',
  approved: 'Approved',
  sent: 'Sent',
  replied: 'Replied',
  meeting: 'Meeting',
  trial: 'Trial',
  won: 'Won',
  lost: 'Lost',
  disqualified: 'Disqualified',
};
